import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const LIMIT_BYTES = 20 * 1024 * 1024;

// The first pass is the sharpest. Stronger passes run only while the file
// is still above the caller's target, so annotation layers can be added later.
const PASSES = [
  { maxEdge: 1800, quality: 0.74 },
  { maxEdge: 1500, quality: 0.64 },
  { maxEdge: 1200, quality: 0.55 },
];

function copyBytes(bytes) {
  if (bytes instanceof Uint8Array) return bytes.slice();
  return new Uint8Array(bytes.slice(0));
}

function yieldToBrowser() {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

function canvasToJpegBytes(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not compress a PDF page."));
          return;
        }
        resolve(new Uint8Array(await blob.arrayBuffer()));
      },
      "image/jpeg",
      quality
    );
  });
}

function prepareCanvas(width, height) {
  const canvas = document.createElement("canvas");
  const attempts = [
    [width, height],
    [Math.max(1, Math.floor(width / 2)), Math.max(1, Math.floor(height / 2))],
  ];
  for (const [nextWidth, nextHeight] of attempts) {
    try {
      canvas.width = nextWidth;
      canvas.height = nextHeight;
      return canvas;
    } catch {
      // The browser refused this bitmap. Try a smaller one.
    }
  }
  throw new Error("Could not allocate a page while compressing the PDF.");
}

async function rebuildPdf(original, settings, { onProgress, isCancelled }) {
  const pdf = await pdfjs.getDocument({ data: original.slice(0) }).promise;
  const out = await PDFDocument.create();

  try {
    const pageCount = pdf.numPages;
    if (!pageCount) {
      throw new Error("This PDF has no pages to compress.");
    }

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      if (isCancelled?.()) return null;
      onProgress?.({ page: pageNumber, pageCount });
      await yieldToBrowser();
      if (isCancelled?.()) return null;

      const page = await pdf.getPage(pageNumber);
      let canvas = null;
      try {
        const unit = page.getViewport({ scale: 1 });
        const longest = Math.max(unit.width, unit.height) || 1;
        const scale = Math.max(0.3, Math.min(settings.maxEdge / longest, 3));
        const viewport = page.getViewport({ scale });
        canvas = prepareCanvas(
          Math.max(1, Math.floor(viewport.width)),
          Math.max(1, Math.floor(viewport.height))
        );
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({
          canvasContext: ctx,
          viewport,
          background: "rgb(255,255,255)",
        }).promise;

        const jpg = await canvasToJpegBytes(canvas, settings.quality);
        const image = await out.embedJpg(jpg);
        const pdfPage = out.addPage([unit.width, unit.height]);
        pdfPage.drawImage(image, {
          x: 0,
          y: 0,
          width: unit.width,
          height: unit.height,
        });
      } finally {
        if (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        }
        page.cleanup();
      }
    }

    if (isCancelled?.()) return null;
    return out.save({ useObjectStreams: true });
  } finally {
    try {
      await pdf.destroy();
    } catch {
      pdf.cleanup?.();
    }
  }
}

export async function compressPdf(sourceBytes, options = {}) {
  const maxBytes = options.maxBytes ?? LIMIT_BYTES;
  const original = copyBytes(sourceBytes);
  let best = null;

  for (const settings of PASSES) {
    if (options.isCancelled?.()) return null;
    const out = await rebuildPdf(original, settings, options);
    if (!out) return null;
    if (!best || out.byteLength < best.byteLength) best = out;
    if (best.byteLength <= maxBytes) break;
  }

  return best;
}
